import {useCallback, useEffect} from "react";
import {batch, useComputed, useSignal} from "@preact/signals-react";
import {useAppToast} from "@ui-seeker/app";
import {postSubmit, isEmpty, scrollToComponent, stringToDate, formatDate, FORMAT_DATE_BACK} from "@ui-seeker/utils";

import {usePricingDiscountRoleConfig} from "../../../@hooks";
import {evolutionSkuDataFilters} from "../../../@signals";
import {VIEW_DATA} from "../@constants";


export default function usePriceDiscountEvolutionData() {
	const { showToast } = useAppToast()
	// ⚠️ `urlApiEvolution`, not `urlApi`: this chart keeps a base url per role, because a
	// retailer's drill-down is a different contract (main-site series and `product_id` in every
	// series name). See the hook.
	const { urlApiEvolution } = usePricingDiscountRoleConfig()

	const isLoading = useSignal(false)
	const averageMarketData = useSignal({})
	const skusSelected = useSignal([])
	const topRetailersData = useSignal({})
	const fiveRetailers = useSignal({})
	const currentSku = useSignal(null)
	const viewData = useSignal(VIEW_DATA.TOP_RETAILERS)
    const dateRange = useSignal([])
	const currency = useSignal('')

	const currentSkuSelected = useComputed(() => skusSelected.value.filter(item => item.skuID === currentSku.value)[0] || {})
	const isViewTopRetailersData = useComputed(() => viewData.value === VIEW_DATA.TOP_RETAILERS)
	const data = useComputed(() =>
		isViewTopRetailersData.value
			? topRetailersData.value[currentSku.value] || []
			: averageMarketData.value[currentSku.value] || []
	)

	useEffect(() => {
		if (!isEmpty(evolutionSkuDataFilters.value)) batch(() => {
			isLoading.value = true
			averageMarketData.value = {}
		 	skusSelected.value  = []
		 	topRetailersData.value  = {}
		 	fiveRetailers.value  = {}
		 	currentSku.value  = null
			showToast.promise(postSubmit(`${urlApiEvolution}/evolution`, evolutionSkuDataFilters.value).then(res => batch(() => {
				averageMarketData.value = res.averageMarket
				skusSelected.value = res.skusSelected
				topRetailersData.value = res.topRetailers
				fiveRetailers.value = res.fiveRetailers
				currentSku.value = (res.skusSelected[0] || {}).skuID
				currency.value = res.currencyChart
                dateRange.value = [stringToDate(evolutionSkuDataFilters.value.startDate), stringToDate(evolutionSkuDataFilters.value.endDate)]
				isLoading.value = false
			})).catch(() => {
				isLoading.value = false
				showToast.error()
			})
			.finally(() => scrollToComponent('evolution-sku-chart')))
		}) 

		if (isEmpty(evolutionSkuDataFilters.value)) batch( () => {
			averageMarketData.value = {}
		 	skusSelected.value  = []
		 	topRetailersData.value  = {}
		 	fiveRetailers.value  = {}
		 	currentSku.value  = null
		})

	}, [JSON.stringify(evolutionSkuDataFilters.value)]);

    const onChange = (e) => {
        batch(() => {
            dateRange.value = e
            evolutionSkuDataFilters.value = {
                ...evolutionSkuDataFilters.value,
                startDate: formatDate(e[0], { format: FORMAT_DATE_BACK }),
                endDate: formatDate(e[1], { format: FORMAT_DATE_BACK }),
            }
        })
    }

	const resetEvolutionSkuData = () => {
		averageMarketData.value = {}
		skusSelected.value  = []
		topRetailersData.value  = {}
		fiveRetailers.value  = {}
		currentSku.value  = null
		evolutionSkuDataFilters.value = {}
	}

	const handleSkuSelected = useCallback(newValue => {
		currentSku.value = newValue
	}, [])

	const handleViewData = useCallback(newValue => {
		viewData.value = newValue
	}, [])

	return {
		isLoading: isLoading.value,
		averageMarketData: averageMarketData.value,
		skusSelected: skusSelected.value,
		topRetailersData: topRetailersData.value,
		fiveRetailers: fiveRetailers.value,
		currentFiveRetailers: fiveRetailers.value[currentSku.value] || [],
		currentSku: currentSku.value,
        dateRange: dateRange.value,

		currentSkuSelected: currentSkuSelected.value,
		data: data.value,
		viewData: viewData.value,
		isViewTopRetailersData: isViewTopRetailersData.value,

		currency: currency.value,

        onChange, handleSkuSelected, handleViewData, resetEvolutionSkuData
	}
}